import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { alumniPhotoUrl } from '../../utils/assets';
import { Users } from 'lucide-react';

const AlumniCarousel = ({ autoScroll = true, scrollSpeed = 0.8 }) => {
  const navigate = useNavigate();
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const animationRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    fetchAlumni();
  }, []);

  useEffect(() => {
    if (autoScroll && alumni.length > 0 && scrollContainerRef.current && !isDragging) {
      startAutoScroll();
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [alumni, autoScroll, isDragging]);

  const fetchAlumni = async () => {
    try {
      const response = await api.get('/alumni');
      console.log('Fetched alumni:', response.data);
      setAlumni(response.data);
    } catch (error) {
      console.error('Error fetching alumni:', error);
    } finally {
      setLoading(false);
    }
  };

  const startAutoScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollAmount = container.scrollLeft;
    
    const scroll = () => {
      if (container && !isDragging) {
        scrollAmount += scrollSpeed;
        container.scrollLeft = scrollAmount;
        
        if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 1) {
          scrollAmount = 0;
          container.scrollLeft = 0;
        }
        animationRef.current = requestAnimationFrame(scroll);
      }
    };
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    animationRef.current = requestAnimationFrame(scroll);
  };

  const stopAutoScroll = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };

  // Mouse/Touch drag scrolling handlers
  const handleMouseDown = (e) => {
    stopAutoScroll();
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    scrollContainerRef.current.style.cursor = 'grab';
    if (autoScroll) {
      startAutoScroll();
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      scrollContainerRef.current.style.cursor = 'grab';
      if (autoScroll) {
        startAutoScroll();
      }
    }
  };

  // Touch events for mobile
  const handleTouchStart = (e) => {
    stopAutoScroll();
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (autoScroll) {
      startAutoScroll();
    }
  };

  const handleCardClick = (id) => {
    if (!isDragging) {
      navigate(`/alumni/${id}`);
    }
  };

  const getRoleBadgeClass = (role) => {
    if (['Chairman', 'Vice Chairman', 'Patron'].includes(role)) return 'badge-gold';
    if (['Secretary', 'Treasurer', 'Organizing Secretary', 'Advisor'].includes(role)) {
      return 'badge-navy';
    }
    return 'badge-neutral';
  };

  const getImageUrl = alumniPhotoUrl;

  if (loading) {
    return (
      <div className="flex gap-5 overflow-hidden">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="w-[280px] flex-shrink-0">
            <div className="overflow-hidden rounded-xl border border-ink-200 bg-white">
              <div className="aspect-[4/3] animate-pulse bg-ink-200" />
              <div className="space-y-2 p-4">
                <div className="h-4 w-2/3 animate-pulse rounded bg-ink-200" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-ink-100" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (alumni.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-ink-300 bg-white py-12 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
          <Users className="h-6 w-6" aria-hidden="true" />
        </span>
        <h3 className="mt-4 text-base font-semibold text-ink-900">No alumni listed yet</h3>
        <p className="mt-1 text-sm text-ink-500">
          Committee members will appear here once they have been added.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      <div 
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="overflow-x-auto scrollbar-hide select-none"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          cursor: 'grab',
          overflowX: 'auto',
          scrollBehavior: 'smooth'
        }}
      >
        <style>
          {`
            .scrollbar-hide::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        <div className="flex gap-5 py-2" style={{ width: 'max-content' }}>
          {alumni.map((alumnus) => {
            const imageUrl = getImageUrl(alumnus.photo_url);

            return (
              <div
                key={alumnus.id}
                className="group w-[280px] flex-shrink-0"
              >
                <button
                  type="button"
                  onClick={() => handleCardClick(alumnus.id)}
                  className="flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-xl border border-ink-200 bg-white text-left shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-brand-800">
                    <span
                      className="absolute inset-0 flex items-center justify-center text-3xl font-bold text-white/70"
                      aria-hidden="true"
                    >
                      {alumnus.name?.charAt(0)}
                    </span>
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={`Portrait of ${alumnus.name}`}
                        className="relative h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                        draggable="false"
                      />
                    ) : null}
                  </div>

                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="min-w-0 truncate font-semibold text-ink-900">
                        {alumnus.name}
                      </h3>
                      <span className={`${getRoleBadgeClass(alumnus.role)} flex-shrink-0`}>
                        {alumnus.role}
                      </span>
                    </div>

                    {alumnus.department_name && (
                      <p className="mt-1 truncate text-sm text-ink-500">
                        {alumnus.department_name}
                      </p>
                    )}

                    {alumnus.graduation_year && (
                      <p className="mt-1 text-xs text-ink-400">
                        Class of {alumnus.graduation_year}
                      </p>
                    )}

                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-700">
                      View profile
                      <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
                        →
                      </span>
                    </span>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AlumniCarousel;
